import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { Url } from '@wix/da-url';
import {
  BiContextProvidedValue,
  BiData,
  ItemClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { getEnv } from '@wix/da-react-app/pkg/client/Environment/redux/selectors';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import ArrowRight from '@wix/da-ds/pkg/Icons/ArrowRight';
import SmallArrowRight from '@wix/da-ds/pkg/Icons/12x12/ArrowRight';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import {
  getActiveSection,
  getCurrentProductsSectionType,
} from '../../../selectors/studio';
import { StudioSection } from '../../../types/store';

import s from './ProductsBreadcrumbs.scss';

interface Props {}

const ProductsBreadcrumbs: FC<Props> = () => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { requestUrl } = useSelector(getEnv);
  const activeSection = useSelector(getActiveSection);
  const isRootLevel = activeSection === StudioSection.PRODUCTS_LOBBY;
  const productType = useSelector(getCurrentProductsSectionType);
  const biCommon = BiData<ItemClickBiEvent>({
    evid: 100,
    sectionname: BiContextProvidedValue,
    page_typeid: BiContextProvidedValue,
    page_itemid: BiContextProvidedValue,
  });

  const separator = isMobile ? (
    <SmallArrowRight
      className={s['breadcrumb-separator']}
      size={IconSize.TINY}
    />
  ) : (
    <ArrowRight className={s['breadcrumb-separator']} size={IconSize.COMPACT} />
  );

  return (
    <div className={s['root']}>
      <PlainButtonOrLink
        key="root"
        className={cn(s['breadcrumb'], s['root-breadcrumb'])}
        href={Url.studio({ products: 'root' })}
        biData={BiData<ItemClickBiEvent>({
          ...biCommon,
        })}
      >
        {t(`studio.products.breadcrumbs.${isRootLevel ? 'lobby' : 'root'}`)}
      </PlainButtonOrLink>
      {!isRootLevel && separator}
      {!isRootLevel && (
        <PlainButtonOrLink
          key="currentSection"
          className={s['breadcrumb']}
          href={requestUrl}
          biData={BiData<ItemClickBiEvent>({
            ...biCommon,
          })}
        >
          {t(`studio.products.breadcrumbs.${productType}`)}
        </PlainButtonOrLink>
      )}
    </div>
  );
};
ProductsBreadcrumbs.displayName = 'ProductsBreadcrumbs';

export default ProductsBreadcrumbs;
